/**
 * Auth module - Zod validation schemas
 */

import { z } from 'zod';
import { USER_ROLES } from '@/lib/roles';

// User role enum (canonical: must match DB user_role)
export const UserRoleSchema = z.enum(USER_ROLES);

// UUID validation
const UUIDSchema = z.string().uuid();

/**
 * Schema for user sign up
 */
export const SignUpSchema = z.object({
  email: z
    .string()
    .trim()
    .email('Please enter a valid email address')
    .max(255, 'Email must be less than 255 characters')
    .toLowerCase(),
  password: z
    .string()
    .min(8, 'Password must be at least 8 characters')
    .max(128, 'Password must be less than 128 characters')
    .regex(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
      'Password must contain at least one uppercase letter, one lowercase letter, and one number'
    ),
  fullName: z
    .string()
    .trim()
    .min(2, 'Name must be at least 2 characters')
    .max(100, 'Name must be less than 100 characters')
    .regex(/^[a-zA-Z\s\-']+$/, 'Name can only contain letters, spaces, hyphens, and apostrophes'),
  role: UserRoleSchema,
});

export type SignUpInput = z.infer<typeof SignUpSchema>;

/**
 * Schema for user sign in
 */
export const SignInSchema = z.object({
  email: z
    .string()
    .trim()
    .email('Please enter a valid email address')
    .max(255)
    .toLowerCase(),
  password: z.string().min(1, 'Password is required').max(128),
});

export type SignInInput = z.infer<typeof SignInSchema>;

/**
 * Schema for profile update
 */
export const UpdateProfileSchema = z.object({
  full_name: z
    .string()
    .trim()
    .min(2, 'Name must be at least 2 characters')
    .max(100)
    .regex(/^[a-zA-Z\s\-']+$/)
    .optional(),
  phone: z
    .string()
    .trim()
    .regex(/^(?:\+61|0)[2-478](?:\d{8})$/, 'Please enter a valid Australian phone number')
    .optional()
    .or(z.literal('')),
  address: z.string().trim().max(500).optional(),
  suburb: z.string().trim().max(100).optional(),
  state: z.string().trim().max(50).optional(),
  postcode: z.string().trim().regex(/^\d{4}$/, 'Postcode must be 4 digits').optional(),
  avatar_url: z.string().url().max(1000).optional().or(z.literal('')),
});

export type UpdateProfileInput = z.infer<typeof UpdateProfileSchema>;

/**
 * Schema for tradie profile
 */
export const TradieProfileSchema = z.object({
  business_name: z.string().trim().max(200).optional(),
  abn: z
    .string()
    .trim()
    .regex(/^\d{11}$/, 'ABN must be 11 digits')
    .optional()
    .or(z.literal('')),
  licence_number: z.string().trim().max(50).optional(),
  licence_state: z.string().trim().max(10).optional(),
  bio: z.string().trim().max(2000).optional(),
  hourly_rate: z.number().positive().max(1000).optional(),
  service_radius_km: z.number().int().positive().max(500).optional(),
  service_areas: z.array(z.string().trim().max(100)).max(50).optional(),
  skills: z.array(z.enum([
    'electrical',
    'plumbing',
    'carpentry',
    'handyman',
    'painting',
    'roofing',
    'landscaping',
    'hvac',
    'flooring',
    'tiling',
  ])).optional(),
  insurance_policy_number: z.string().trim().max(100).optional(),
  insurance_expiry: z.string().datetime().optional(),
});

export type TradieProfileInput = z.infer<typeof TradieProfileSchema>;

/**
 * Validate sign up input
 */
export function validateSignUp(data: unknown): SignUpInput {
  return SignUpSchema.parse(data);
}

/**
 * Validate sign in input
 */
export function validateSignIn(data: unknown): SignInInput {
  return SignInSchema.parse(data);
}

/**
 * Safe parse functions
 */
export function safeValidateSignUp(data: unknown) {
  return SignUpSchema.safeParse(data);
}

export function safeValidateSignIn(data: unknown) {
  return SignInSchema.safeParse(data);
}

export function safeValidateProfile(data: unknown) {
  return UpdateProfileSchema.safeParse(data);
}
